"use client";
import {
  Carousel,
  CarouselIconButton,
  CarouselSlide,
  useCarousel,
} from "components/individuals/blocks/featured-carousel/carousel-elements";
import {
  Box,
  Circle,
  Flex,
  HStack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { JSX, useEffect, useState } from "react";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { colors } from "@areahub/theme";
import styled from "styled-components";
import Link from "next/link";

export const LargeSpanText = styled.span`
  font-size: 22px;
  font-weight: bold;
  line-height: 1;
  @media (min-width: 768px) {
    font-size: 30px;
  }
`;

interface EnterpriseCarouselProps {
  slideContent: { description: string; citation?: string }[];
  activeIndicatorColor?: string;
  nonActiveIndicatorColor?: string;
}

export const EnterpriseCarousel = ({
  slideContent,
  activeIndicatorColor,
  nonActiveIndicatorColor,
}: EnterpriseCarouselProps) => {
  const [isMdBp] = useMediaQuery("(min-width: 768px)");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setLoaded] = useState(false);
  const [isMouseHover, setIsMouseHover] = useState(false);
  const [sliderRef, slider] = useCarousel({
    loop: true,
    slideChanged: (slider) => setCurrentSlide(slider.track.details.rel),
    created: () => setLoaded(true),
  });

  /**
   * Controls the autoscrolling behavior of the carousel component.
   * The autoscrolling behavior stops when the mouse hovers over the carousel,
   * and resumes when the mouse leaves the component.
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    function autoscroll() {
      if (slider.current && slider.current.track.details.slides.length < 2) {
        return;
      }
      if (!isMouseHover) {
        interval = setInterval(() => {
          slider.current?.next();
        }, 9000);
      }
    }

    autoscroll();

    return () => {
      clearInterval(interval);
    };
  }, [slider, isMouseHover]);

  function prevSlide() {
    slider.current?.prev();
  }
  function nextSlide() {
    slider.current?.next();
  }
  function setSlide(idx: number) {
    slider.current?.moveToIdx(idx);
  }

  /**
   * Parses a string and wraps text inside `<LargeSpanText>` components wherever `<LargeSpanText>` tags appear in the input string.
   *
   * @param {string} content - The input string containing `<LargeSpanText>` tags and text.
   * @returns {(string | JSX.Element)[]} An array of strings and `<LargeSpanText>` elements.
   *
   * @example
   * ```
   * const content = "Hello, my name is <LargeSpanText>Jane Doe</LargeSpanText>!";
   * const output = renderLargeText(content);
   * // Output: ["Hello, my name is", <LargeSpanText key={1}>Jane Doe</LargeSpanText>, "!"]
   * ```
   */
  function renderLargeText(content: string): (string | JSX.Element)[] {
    return content
      .split(/<LargeSpanText>|<\/LargeSpanText>/g)
      .map((part, idx) =>
        idx % 2 === 1 ? <LargeSpanText key={idx}>{part}</LargeSpanText> : part,
      );
  }

  return (
    <Flex flexDir={"column"}>
      <Box
        w={{
          base: "100vw",
          md: "360px",
          lg: "400px",
          xl: "430px",
        }}
        pt={3}
        pb={8}
        position="relative"
        bgColor={colors.enterpriseColors.blue["500"]}
        borderRadius={{
          base: "none",
          md: "3xl",
        }}
        mt={{
          base: 5,
          lg: 0,
        }}
        mr={{
          lg: 12,
        }}
      >
        <Carousel
          id="carousel"
          ref={sliderRef}
          mx="auto"
          aria-label="Real World Impacts Facts Carousel"
          onMouseEnter={() => setIsMouseHover(true)}
          onMouseLeave={() => setIsMouseHover(false)}
        >
          {slideContent?.map(({ description, citation }, i) => {
            const slideLength = slideContent.length;
            const citationArr = citation?.split("|");
            const source = citationArr?.[0];
            const articleTitle = citationArr?.[1];
            const sourceUrl = citationArr?.[2];
            return (
              <CarouselSlide
                key={i}
                textAlign="center"
                opacity={currentSlide === i ? 1 : 0.4}
                slideLength={slideLength}
                idx={i}
              >
                <Flex
                  w="85%"
                  mx="auto"
                  pl={3.5}
                  flexDir={"column"}
                  justify={"space-between"}
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "md",
                      lg: "lg",
                      xl: "20px",
                    }}
                    fontWeight="normal"
                    mt="20px"
                    pt={{
                      base: 5,
                      md: 0,
                    }}
                    textAlign="left"
                  >
                    {renderLargeText(description)}
                  </Text>
                  {citation ? (
                    <Text
                      color={"white"}
                      textAlign="left"
                      fontSize={"xs"}
                      mt={5}
                    >
                      {source}.{" "}
                      {articleTitle && (
                        <Box as={"span"} fontStyle={"italic"}>
                          {articleTitle}.
                        </Box>
                      )}{" "}
                      {source && sourceUrl && (
                        <Link href={sourceUrl as string} target="_blank">
                          {source}
                        </Link>
                      )}
                      .
                    </Text>
                  ) : (
                    <></>
                  )}
                </Flex>
              </CarouselSlide>
            );
          })}
        </Carousel>
        {isLoaded && slideContent?.length > 1 && (
          <>
            <CarouselIconButton
              aria-controls="carousel"
              aria-label="Previous Slide"
              color="white"
              position="absolute"
              left="2px"
              bottom="45%"
              onClick={prevSlide}
              icon={<IoChevronBackOutline />}
            />
            <CarouselIconButton
              aria-controls="carousel"
              aria-label="Next Slide"
              color="white"
              position="absolute"
              bottom="45%"
              right="2px"
              onClick={nextSlide}
              icon={<IoChevronForwardOutline />}
            />
          </>
        )}
        {!isMdBp && slideContent?.length > 1 ? (
          <HStack mx="auto" justify="center" mt="20px" spacing="15px">
            {slideContent?.map((_, idx) => {
              const isSelected = currentSlide == idx;
              return (
                <Circle
                  aria-label={`Go to slide ${idx + 1}`}
                  aria-current={isSelected ? "true" : undefined}
                  key={idx}
                  size={{
                    base: "8px",
                    lg: "12px",
                  }}
                  bgColor={
                    isSelected
                      ? (activeIndicatorColor ??
                        colors.enterpriseColors.blue["50"])
                      : (nonActiveIndicatorColor ??
                        colors.enterpriseColors.blue["100"])
                  }
                  cursor="pointer"
                  onClick={() => setSlide(idx)}
                />
              );
            })}
          </HStack>
        ) : (
          <></>
        )}
      </Box>
      {isMdBp && slideContent?.length > 1 ? (
        <HStack
          mx="auto"
          justify="center"
          mt="20px"
          spacing="15px"
          pr={{
            lg: 14,
          }}
        >
          {slideContent?.map((_, idx) => {
            const isSelected = currentSlide == idx;
            return (
              <Circle
                aria-label={`Go to slide ${idx + 1}`}
                aria-current={isSelected ? "true" : "false"}
                key={idx}
                size={{
                  base: "8px",
                  md: "10px",
                  lg: "11px",
                  xl: "12px",
                }}
                bgColor={
                  isSelected
                    ? (activeIndicatorColor ??
                      colors.enterpriseColors.blue["100"])
                    : (nonActiveIndicatorColor ??
                      colors.enterpriseColors.blue["50"])
                }
                cursor="pointer"
                onClick={() => setSlide(idx)}
              />
            );
          })}
        </HStack>
      ) : (
        <></>
      )}
    </Flex>
  );
};
